/**
 * ponState — THE single source of truth for the Pon animation state map.
 *
 * Both render layers derive from this ONE table:
 *  - the canvas stage (`ponCharacter.ts` Phaser tweens read `PON_STATE_VISUAL`),
 *  - the DOM plaza (`LobbyScene.ts` reads `CSS_CLASS_FOR[ponStateFrom(...)]`).
 *
 * Before this module the two were two parallel mappings that drifted (the plaza
 * hand-rolled `if`s against `platformer.ponState` = `idle|walk|jump|fall`; the
 * stage used `ponStateFrom`). They now both funnel through `ponStateFrom`'s
 * vocabulary so they can never disagree, and `PON_STATE_VISUAL` is authored to
 * MATCH the `ponpon.css` `.pon-*` keyframes so the canvas tweens read identical
 * to the plaza CSS. **Client-visual only** — pure mapping, never on the wire.
 */

export type PonAnimState =
  | 'idle'
  | 'run'
  | 'jump'
  | 'fall'
  | 'absorbing'
  | 'absorbed'
  | 'carrying';

/**
 * Whether a given state shows the big open absorb mouth (→ pick the texture /
 * CSS hook). Only the *channelling* Pon (`absorbing`) gulps with the open mouth.
 * A Pon that has BEEN absorbed (`absorbed` — pinned into a carry/bridge) keeps
 * its normal happy closed mouth so it reads "held / squished-content".
 *
 * NOTE: the plaza's `.pon-absorbed` CSS keeps the absorb mouth visible (it is the
 * crate-latched "holding" pose, not the pinned-teammate pose). The stage's
 * `absorbed` is the pinned-teammate pose → closed mouth. This `mouthOpen` flag
 * drives the *canvas* texture pick only; the plaza mouth is governed by its CSS.
 */
export function mouthOpen(state: PonAnimState): boolean {
  return state === 'absorbing';
}

/**
 * Canonical per-state visual recipe (durations / scale / rotate / lift),
 * authored to MATCH `client/src/styles/ponpon.css` `.pon-*` keyframes.
 *
 * - `durationMs` is the FULL keyframe period (the CSS `animation` duration). A
 *   yoyo tween therefore runs `durationMs / 2` per half-cycle.
 * - `squash` is the peak `scale(x, y)` of the animation (relative to 1).
 * - `rotateDeg` / `liftPx` are the peak lean / translateY.
 * - `mouthOpen` mirrors {@link mouthOpen}.
 *
 * Static held poses (`jump`/`fall`/`absorbed`) carry their held scale in
 * `squash`; `durationMs`/`rotateDeg`/`liftPx` describe any residual motion.
 */
export interface PonStateVisual {
  /** Full CSS keyframe period in ms (e.g. idle 2800, run 420). */
  durationMs: number;
  /** Peak [scaleX, scaleY] of the animation. */
  squash: [number, number];
  /** Peak lean in degrees. */
  rotateDeg: number;
  /** Peak vertical lift in px (negative = up). */
  liftPx: number;
  mouthOpen: boolean;
}

/**
 * The state→visual table. Values reconciled to `ponpon.css`:
 *  - idle:      `.pon-idle` `pon-breathe` 2.8s → scale(1.035, 0.965)            (css:151,158)
 *  - run:       `.pon-walk` 0.42s → lift −3px, scale(0.95,1.06)..(1.07,0.93), ±4° (css:155,167)
 *  - jump:      `.pon-jump` scale(0.9, 1.14)                                     (css:196)
 *  - fall:      `.pon-fall` scale(1.08, 0.9)                                     (css:200)
 *  - absorbing: `.pon-absorbing` `pon-absorb-hold` 0.34s → scale(1.07..1.11, 0.95..0.92), ±2° (css:262,265)
 *  - absorbed:  `.pon-absorbed` static scale(1.05, 0.97)                         (css:290)
 *  - carrying:  no CSS equivalent (stage-only) — a planted slightly-squashed trudge.
 */
export const PON_STATE_VISUAL: Record<PonAnimState, PonStateVisual> = {
  idle: { durationMs: 2800, squash: [1.035, 0.965], rotateDeg: 0, liftPx: 0, mouthOpen: false },
  run: { durationMs: 420, squash: [1.07, 0.93], rotateDeg: 4, liftPx: -3, mouthOpen: false },
  jump: { durationMs: 0, squash: [0.9, 1.14], rotateDeg: 0, liftPx: 0, mouthOpen: false },
  fall: { durationMs: 0, squash: [1.08, 0.9], rotateDeg: 0, liftPx: 0, mouthOpen: false },
  absorbing: { durationMs: 340, squash: [1.11, 0.92], rotateDeg: 2, liftPx: 0, mouthOpen: true },
  absorbed: { durationMs: 0, squash: [1.05, 0.97], rotateDeg: 0, liftPx: 0, mouthOpen: false },
  carrying: { durationMs: 300, squash: [1.04, 0.96], rotateDeg: 0, liftPx: -2, mouthOpen: false },
};

/**
 * State → plaza CSS class (the `.pon-*` hook stamped on `.pon-fig`). The plaza
 * adapter (`LobbyScene`) reads `CSS_CLASS_FOR[ponStateFrom(...)]` so the DOM Pon
 * is driven by the SAME state machine as the canvas Pon.
 *
 * The plaza only ever produces `idle/run/jump/fall/absorbing/absorbed`
 * (it has no server `carrying` channel — a crate-latched plaza Pon maps to
 * `absorbed`, the steady "holding" CSS pose). `carrying` maps to `pon-absorbed`
 * for completeness so the lookup is total.
 */
export const CSS_CLASS_FOR: Record<PonAnimState, string> = {
  idle: 'pon-idle',
  run: 'pon-walk',
  jump: 'pon-jump',
  fall: 'pon-fall',
  absorbing: 'pon-absorbing',
  absorbed: 'pon-absorbed',
  carrying: 'pon-absorbed',
};

/**
 * Map the Pon flags to an animation state. Pure (testable); the single place the
 * Absorb verb's state→pose mapping lives, so every render layer agrees.
 *
 * Priority: absorbed (pinned) > carrying (holding crate/teammate) > absorbing
 * (local channel) > airborne (jump/fall) > run > idle.
 */
export function ponStateFrom(flags: {
  grabbing: string;
  chainPrev: string;
  chainNext: string;
  moving: boolean;
  vy: number;
  grounded: boolean;
  /** Local-only: this client is channelling an Absorb this frame. */
  channeling: boolean;
}): PonAnimState {
  if (flags.chainPrev !== '') return 'absorbed';
  if (flags.grabbing !== '' || flags.chainNext !== '') return 'carrying';
  if (flags.channeling) return 'absorbing';
  if (!flags.grounded) return flags.vy < 0 ? 'jump' : 'fall';
  return flags.moving ? 'run' : 'idle';
}
